import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

import styles from './TimelineStop.module.scss';
import { TripStop } from '../../../../../types/trip.types';
import { getActivityIcon, getActivityStatus, getActivityGradient, DayStatus } from '../../utils/activityUtils';
import { getActivityStartTime } from '../../utils/activityFormatters';
import { useAppSelector } from '../../../../../hooks/redux';
import { useCurrencySymbol } from '../../../../../hooks/useCurrencySymbol';

interface TimelineStopProps {
  activity: TripStop;
  dayStatus: DayStatus;
  isLast?: boolean;
  onShowDetails: (activity: TripStop) => void;
  isSelected: boolean;
  onCloseDetails: () => void;
}

const TimelineStop: FC<TimelineStopProps> = ({
  activity,
  dayStatus,
  isLast = false,
  onShowDetails,
  isSelected,
  onCloseDetails,
}) => {
  const status = getActivityStatus(activity);
  const user = useAppSelector(state => state.user);
  const currencySymbol = useCurrencySymbol();
  const IconComponent = getActivityIcon(activity.stop_type);

  const iconStatus = dayStatus === 'upcoming' ? 'upcoming' : 'active';

  const handleShowMoreClick = () => {
    if (isSelected) {
      onCloseDetails();
    } else {
      onShowDetails(activity);
    }
  };

  const startTime = getActivityStartTime(activity);

  const getActivityTitle = () => {
    switch (activity.stop_type) {
      case 'Flight':
        return `${activity.flight_from} - ${activity.flight_to}`;
      case 'BorderCrossing':
        return `${activity.country_from} -> ${activity.country_to}`;
      case 'Transportation':
        return activity.vehicle_type || 'Transportation';
      default:
        return (activity as any).name || (activity as any).place || activity.stop_type;
    }
  };

  const getLocationOrRoute = () => {
    switch (activity.stop_type) {
      case 'Flight':
        return `${activity.flight_from} -> ${activity.flight_to}`;
      case 'BorderCrossing':
        return `${activity.country_from} -> ${activity.country_to}`;
      case 'Hotel':
        return activity.address?.name || null;
      case 'CarRental':
        return activity.pick_up_address?.name
          ? `Pickup: ${activity.pick_up_address.name}`
          : activity.drop_off_address?.name || null;
      case 'Ferry':
      case 'Cruise':
      case 'Rail':
      case 'Transportation':
        return (activity as any).location_from_name && (activity as any).location_to_name
          ? `${(activity as any).location_from_name} -> ${(activity as any).location_to_name}`
          : (activity as any).location_from_name || (activity as any).location_to_name || null;
      default:
        return (activity as any).address?.name || (activity as any).place || null;
    }
  };

  const getProvider = () => {
    switch (activity.stop_type) {
      case 'Flight':
        return activity.airline || null;
      case 'Hotel':
        return 'Hotel';
      case 'CarRental':
        return activity.transport_name || 'Car Rental';
      case 'Ferry':
      case 'Transportation':
        return (activity as any).carrier_name || null;
      case 'Cruise':
        return (activity as any).transport_name || null;
      default:
        return null;
    }
  };

  const getFlightNumber = () => (activity.stop_type === 'Flight' ? activity.flight_number || null : null);

  const getBookingNumber = () => ((activity as any).booking ? String((activity as any).booking) : null);

  const title = getActivityTitle();
  const locationOrRoute = getLocationOrRoute();
  const provider = getProvider();
  const flightNumber = getFlightNumber();
  const bookingNumber = getBookingNumber();
  const shouldShowBudget = activity.amount_spend > 0 && user.show_trip_budgets;

  const renderActivityContent = () => {
    return (
      <>
        <Typography
          variant="h9Semibold"
          className={styles.activityType}
        >
          {activity.stop_type}
        </Typography>

        <Typography
          variant="h11Semibold"
          className={styles.activityTitle}
        >
          {title}
        </Typography>

        {locationOrRoute && activity.stop_type !== 'Flight' && activity.stop_type !== 'BorderCrossing' && (
          <Typography
            variant="h11"
            className={styles.activitySubtitle}
          >
            <span className={styles.boldText}>{locationOrRoute}</span>
          </Typography>
        )}

        {provider && (
          <Typography
            variant="h11"
            className={styles.activitySubtitle}
          >
            {provider}
          </Typography>
        )}

        {flightNumber && (
          <Typography
            variant="h11"
            className={styles.activitySubtitle}
          >
            Flight <span className={styles.boldText}>{flightNumber}</span>
          </Typography>
        )}

        {bookingNumber && (
          <Typography
            variant="h11"
            className={styles.activitySubtitle}
          >
            Booking: <span className={styles.boldText}>{bookingNumber}</span>
          </Typography>
        )}

        {shouldShowBudget && (
          <Box className={styles.budgetRow}>
            <Typography
              variant="h10Bold"
              className={styles.budgetLabel}
            >
              Budget
            </Typography>
            <Typography
              variant="h10Semibold"
              className={styles.budgetAmount}
            >
              {currencySymbol}
              {activity.amount_spend}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box className={`${styles.timelineStop} ${styles[status]} ${isSelected ? styles.selected : ''} ${isLast ? styles.last : ''}`}>
      <Box className={`${styles.timelineTime} ${dayStatus === 'upcoming' ? styles.timeUpcoming : ''}`}>
        <Typography
          variant="h12"
          className={styles.timeText}
        >
          {startTime}
        </Typography>
      </Box>
      <Box
        className={`${styles.timelineIcon} ${
          styles[`icon${iconStatus.charAt(0).toUpperCase() + iconStatus.slice(1)}`]
        }`}
      >
        <IconComponent
          size={40}
          gradient={iconStatus === 'active' ? getActivityGradient(activity.id) : undefined}
        />
      </Box>
      <Box className={styles.activityCard}>
        <button
          className={styles.showMoreButton}
          onClick={handleShowMoreClick}
        >
          <Typography
            variant="h11"
            className={styles.showMoreText}
          >
            {isSelected ? 'Show less' : 'Show more'}
          </Typography>
          {isSelected ? (
            <KeyboardArrowLeftIcon className={styles.chevronIcon} />
          ) : (
            <KeyboardArrowRightIcon className={styles.chevronIcon} />
          )}
        </button>
        <Box className={styles.activityContent}>{renderActivityContent()}</Box>
      </Box>
    </Box>
  );
};

export default TimelineStop;
